import React, { useRef, useState, useEffect } from "react";
import { useDrag } from "react-dnd";
import { getChildIcon } from "./utils.js";

const StudentCell = ({ student, day, hour, onManageStudent, columnIndex = 0, totalColumns = 1 }) => {
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ horizontal: 'right', vertical: 'bottom' });

  const [{ isDragging }, drag] = useDrag(() => {
    // Find the coach for this specific assignment (day/hour)
    let coachForThisSlot = student.coach; // Default to legacy coach field

    if (Array.isArray(student.assignments)) {
      const assignment = student.assignments.find(a =>
        String(a.day).trim() === String(day).trim() && Number(a.hour) === hour
      );
      if (assignment && assignment.coach) {
        coachForThisSlot = assignment.coach;
      }
    }

    return {
      type: "STUDENT",
      item: {
        id: student._id,
        coach: coachForThisSlot,
        fromDay: day,  // Track where we're dragging FROM
        fromHour: hour // Track where we're dragging FROM
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    };
  }, [student._id, student.coach, student.assignments, day, hour]);

  const isAvailable =
    Array.isArray(student.availableTimes) &&
    student.availableTimes.some((time) => String(time) === `${day} ${hour}`);

  // Check if student has multiple assignments
  const hasMultipleAssignments =
    Array.isArray(student.assignments) && student.assignments.length > 1;

  // Calculate tooltip position based on column and viewport space
  useEffect(() => {
    const calculatePosition = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Horizontal positioning
      let horizontal;
      if (totalColumns === 1) {
        // Single column: check absolute viewport position to prevent cut-off
        const isLeftSideOfScreen = rect.left < viewportWidth / 2;
        horizontal = isLeftSideOfScreen ? 'right' : 'left';
      } else {
        // Multiple columns: use relative column position
        const isLeftHalf = columnIndex < Math.ceil(totalColumns / 2);
        horizontal = isLeftHalf ? 'right' : 'left';
      }

      // Vertical positioning: check if there's space below (default) or need to show above
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedTooltipHeight = 200; // Approximate tooltip height

      const vertical = spaceBelow > estimatedTooltipHeight ? 'bottom' :
                      (spaceAbove > estimatedTooltipHeight ? 'top' : 'bottom');

      setTooltipPosition({ horizontal, vertical });
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [columnIndex, totalColumns]);

  const handleRightClick = (e) => {
    e.preventDefault(); // Prevent browser context menu
    e.stopPropagation(); // Prevent cell click event
    if (onManageStudent) {
      onManageStudent(student);
    }
  };

  // Combine drag ref and container ref
  const combinedRef = (el) => {
    drag(el);
    containerRef.current = el;
  };

  return (
    <div
      ref={combinedRef}
      className={`draggable-student ${isAvailable ? "" : "not-available"}`}
      onContextMenu={handleRightClick}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.5 : 1
      }}
    >
      {student.firstName} {student.lastName} {getChildIcon(student)}
      {hasMultipleAssignments && (
        <span className="multi-assignment-badge" title={`${student.assignments.length} Kurse`}>
          ({student.assignments.length}×)
        </span>
      )}
      <div
        ref={tooltipRef}
        className={`student-tooltip tooltip-${tooltipPosition.horizontal} tooltip-${tooltipPosition.vertical}`}
      >
        <div className="student-tooltip-name">
          {student.firstName} {student.lastName}
        </div>
        <div className="student-tooltip-detail">
          <span className="student-tooltip-label">Typ:</span>
          <span className="student-tooltip-value">
            {student.adult ? "Erwachsen" : "Kind"}
          </span>
        </div>
        {student.adult ? (
          <div className="student-tooltip-detail">
            <span className="student-tooltip-label">Spielstärke:</span>
            <span className="student-tooltip-value">
              {student.skillLevel || "-"}
            </span>
          </div>
        ) : (
          <div className="student-tooltip-detail">
            <span className="student-tooltip-label">Gruppe:</span>
            <span className="student-tooltip-value">
              {getChildIcon(student)} {student.trainigGroup || "-"}
            </span>
          </div>
        )}
        {!student.adult && (
          <div className="student-tooltip-detail">
            <span className="student-tooltip-label">Teamspieler:</span>
            <span className="student-tooltip-value">
              {student.team === true ? "Ja" : "Nein"}
            </span>
          </div>
        )}
        <div className="student-tooltip-detail" style={{ alignItems: 'flex-start' }}>
          <span className="student-tooltip-label">Verfügbare Zeiten:</span>
          <span className="student-tooltip-value" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {Array.isArray(student.availableTimes) && student.availableTimes.length > 0
              ? (() => {
                  // Group times by day
                  const dayAbbrev = {
                    Montag: "Mo",
                    Dienstag: "Di",
                    Mittwoch: "Mi",
                    Donnerstag: "Do",
                    Freitag: "Fr",
                    Samstag: "Sa",
                  };
                  const grouped = {};
                  student.availableTimes.forEach((time) => {
                    const parts = time.split(" ");
                    if (parts.length === 2) {
                      const day = parts[0];
                      const hour = parts[1];
                      if (!grouped[day]) grouped[day] = [];
                      grouped[day].push(hour);
                    }
                  });
                  // Format as separate rows, one per day
                  return Object.keys(grouped).map((day, index) => (
                    <span key={index}>{dayAbbrev[day] || day} {grouped[day].join(",")}</span>
                  ));
                })()
              : "-"}
          </span>
        </div>
        {/* Show other assignments if student has multiple assignments */}
        {Array.isArray(student.assignments) && student.assignments.length > 1 && (
          <div className="student-tooltip-detail" style={{ alignItems: 'flex-start', borderTop: '1px solid rgba(255, 255, 255, 0.3)', paddingTop: '6px', marginTop: '6px' }}>
            <span className="student-tooltip-label">Alle Kurse ({student.assignments.length}):</span>
            <span className="student-tooltip-value" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {student.assignments.map((assignment, idx) => {
                // Highlight current assignment
                const isCurrent = String(assignment.day).trim() === String(day).trim() && Number(assignment.hour) === hour;
                return (
                  <span key={idx} style={{ fontWeight: isCurrent ? 'bold' : 'normal' }}>
                    {isCurrent && '→ '}{assignment.day} {assignment.hour}:00
                  </span>
                );
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCell;
